<template>
  <div class="setup-wizard-general-info">
    <p class="setup-wizard-general-info__desc">
      {{ cards[activeCard].desc }}
    </p>
    <template>
      <label class="setup-wizard-general-info__input_label" :for="cards[activeCard].id">{{ cards[activeCard].id }}</label>
      <base-select
        v-if="['geo', 'locale'].includes(cards[activeCard].id)"
        class="setup-wizard-general-info__input"
        :headerStyle="{ 'border-width': '2px' }"
        alignLeft
        shiftLabelRight
        isSmartInput
        :smartInputStyle="{ 'padding-left': '20px' }"
        :placeholder="inputPlaceholderText"
        :options="inputOptions"
        v-model="data[cards[activeCard].id]"
        :selectStyle="{ height: '60px !important' }"
        :max-height-list="450"
      />
      <input
        v-else
        type="text"
        v-model="data[cards[activeCard].id]"
        class="setup-wizard-general-info__input"
        :placeholder="inputPlaceholderText"
      >
    </template>
    <div class="navigation-controls">
      <div class="navigation-controls__main">
        <button
          @click="navigateBack"
          class="setup-wizard-general-info__btn setup-wizard-general-info__btn__back"
        >
          <div class="btn-content">
            <span class="btn-arrow btn-arrow-left"></span>
            BACK
          </div>
        </button>
        <button
          @click="onSubmit"
          class="setup-wizard-general-info__btn setup-wizard-general-info__btn__next"
          :class="{'setup-wizard-general-info__btn__submit': activeCard == cards.length - 1}"
          :disabled="isDataUpdating"
        >
          SUBMIT
        </button>
      </div>
      <button
        @click="onSkip"
        class="setup-wizard-general-info__btn setup-wizard-general-info__btn__skip"
      >
        <div class="btn-content">
          SKIP
          <span class="btn-arrow btn-arrow-right"
          v-if="activeCard < cards.length - 1"
          ></span>
        </div>
      </button>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapState } from 'vuex'

const CARDS = [
  {
    id: 'competitor',
    desc: 'You optionally can add a competitor website to track rankings against. This can be added now, or at any time later. (Must be a fully-qualified URL)'
  }
]
export default {
  name: 'CompetitorInfo',
  data () {
    return {
      errorFields: {},
      isDataUpdating: false,
      activeCard: 0,
      data: {
        competitor: ''
      },
      cards: _.cloneDeep(CARDS)
    }
  },
  computed: {
    ...mapState('client', ['geo', 'device', 'locale']),
    inputPlaceholderText () {
      let text = this.cards[this.activeCard].id
      let label = text[0].toUpperCase() + text.slice(1)
      if (text === 'msv') label = label.toUpperCase()
      text = 'Select ' + label
      return text
    },
    inputOptions () {
      return this[this.cards[this.activeCard].id]
    },
    nextButtonText () {
      if (this.activeCard < this.cards.length - 1) {
        return 'NEXT'
      }
      return 'SUBMIT'
    }
  },
  created () {
    // No need to check for clientIdParam since we're not creating project yet
  },
  methods: {
    ...mapActions('accountSetup', ['completeSetup']),
    ...mapActions('client', ['getClientsSettings']),
    ...mapActions(['getCurrentSession']),
    translateCardIndex (idx) {
      if (idx < 0) {
        return 0
      }
      if (idx > this.cards.length - 1) {
        return this.cards.length - 1
      }
      return idx
    },
    async onSubmit () {
      this.isDataUpdating = true
      
      // Store competitor data in Vuex
      this.$store.commit('accountSetup/setCompetitorInfo', {
        ...this.data
      })
      
      // Get all collected data from Vuex
      const generalInfo = this.$store.state.accountSetup.generalInfo
      const localityInfo = this.$store.state.accountSetup.localityInfo
      const competitorInfo = this.$store.state.accountSetup.competitorInfo
      
      // Combine all data for the final API call
      const completeData = {
        ...generalInfo,
        ...localityInfo,
        ...competitorInfo
      }
      
      const response = await this.completeSetup(completeData)
      this.errorFields = response.error_fields || {}
      this.isDataUpdating = false
      
      if (response.is_successful) {
        // Clear the stored data
        this.$store.commit('accountSetup/clearSetupData')
        
        // Update session and navigate to checklist
        await this.getCurrentSession()
        this.setNewClient(response.project_id, true, true)
        this.getClientsSettings(response.project_id)
        this.$router.push({ name: 'account-checklist' })
      }
    },
    navigateBack () {
      // Go back to LocalityInfo on the msv card (last card)
      this.$router.push({ name: 'setup-wizard-locality-info', hash: '#msv' })
    },
    async onSkip () {
      this.isDataUpdating = true
      
      // Store competitor data in Vuex with null competitor
      this.$store.commit('accountSetup/setCompetitorInfo', {
        competitor: '' // Set competitor to empty string
      })
      
      // Get all collected data from Vuex
      const generalInfo = this.$store.state.accountSetup.generalInfo
      const localityInfo = this.$store.state.accountSetup.localityInfo
      const competitorInfo = this.$store.state.accountSetup.competitorInfo
      
      // Combine all data for the final API call
      const completeData = {
        ...generalInfo,
        ...localityInfo,
        ...competitorInfo
      }
      
      const response = await this.completeSetup(completeData)
      this.errorFields = response.error_fields || {}
      this.isDataUpdating = false
      
      if (response.is_successful) {
        // Clear the stored data
        this.$store.commit('accountSetup/clearSetupData')
        
        // Update session and navigate to checklist
        await this.getCurrentSession()
        this.setNewClient(response.project_id, true, true)
        this.getClientsSettings(response.project_id)
        this.$router.push({ name: 'account-checklist' })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .btn-arrow {
    padding: 4px;
    border: solid $rfx-dark-march;
    border-width: 0 2px 2px 0;
    display: inline-block;
    margin-bottom: 1px;
  }
  .btn-arrow-right {
    transform: rotate(315deg);
    border-color: #fff;
  }
  .btn-arrow-left {
    transform: rotate(135deg);
  }

  .navigation-controls {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 10px;

    &__main {
      display: flex;
      justify-content: space-between;
    }
  }

  .setup-wizard-general-info {
    // height: 30vh;
    width: 394px;
    margin: auto;
    background-color: #fff;
    border-radius: 16px;
    box-shadow: 0 6px 16px 0 rgba(96, 97, 112, 0.2), 0 1px 4px 0 rgba(40, 41, 61, 0.06);
    padding: 48px;

    &__btn {
      text-align: center;
      margin: 0 5px;
      flex-grow: 1;
      outline: none;
      border: none;
      padding: 16px 9px;
      line-height: 24px;
      font-size: 16px;
      font-weight: bold;
      color: #fff;
      border-radius: 8px;
      text-decoration: none;

      &:disabled {
        pointer-events: none;
        opacity: .4;
      }

      &__submit {
        background-color: $rfx-dark-march !important;
      }

      &__next {
        background-color: $rfx-bright-turquoise;
      }
      &__back {
        color: #000;
        background-color: $rfx-light-grey;
      }
      &__skip {
        color: #000;
        background-color: $rfx-light-grey;
        margin-top: 10px;
      }
    }

    &__input {
      display: inline-block;
      width: -webkit-fill-available;
      &[type="text"] {
        line-height: 24px;
        border-radius: 4px;
        border: 1px solid $rfx-light-steel;
        border-width: 2px;
        padding: 16px 16px 16px 10px;
        color: $rfx-medium-march !important;
        &::placeholder {
          color: $rfx-silver;
          font-size: 14px;
        }
      }
      margin: 0 5px 15px;

      &:disabled {
        background-color: #7c8e9b40;
      }

      &::placeholder {
        color: $rfx-medium-march;
      }

      &:focus {
        outline: none;
      }

      &_error {
        border-color: $rfx-light-red;
      }

      &_error-message {
        display: inline-block;
        color: $rfx-light-red;
        margin-top: 8px;
        font-size: 14px;
        line-height: 16px;
      }

      &_wrapper {
        margin-bottom: 24px;

        &_error {
          margin-bottom: 16px;
        }

        &_confirm-password {
          margin-bottom: 0 !important;
        }
      }

      &_label {
        text-transform: uppercase;
        font-size: 14px;
        line-height: 16px;
        display: inline-block;
        font-weight: 500;
        color: $rfx-medium-march;
        margin: 0 5px 8px;
      }

      &_icon {
        display: block;
        font-size: 24px;
        color: $rfx-medium-march;
        width: 0;
        height: 0;
        position: relative;
        left: 18px;
        top: -18px;
      }
    }

    &__desc {
      margin: 0 0 24px;
      padding: 0;
      color: $rfx-dark-march;
      font-size: 18px;
      text-align: center;
      height: 50px;
    }
    &__back {
      display: inline-block;
      color: $rfx-dark-march;
      font-size: 14px;
      line-height: 24px;
      font-weight: bold;
      margin-bottom: 24px;
      text-decoration: none;

    }

  }

</style>
